import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { ClientSearchView } from 'app/shared/models/view/client-search-view.model';
import { PageResponse } from '@iapmoit/library.angular.core';
import { PageRequest } from '../../shared/models/request/page-request.model';
import { AuditsSearchRequest } from './models/audits-search-request.model';
import { DatePipe } from '@angular/common';
import { Application, ApplicationIndustryCode, Audit, AuditAddendumTemplate,
    AuditAttachment, AuditDocumentationRequirement, AuditEmailSchedule, AuditNCRDocument,
    AuditorAuditProgramReview, AuditorHistory, AuditorsToAudit, AuditProgramReview,
    Ballot, Client, ClientAdditionalIAFCode, ClientAdditionalSite, EmailHistory,
    ExpenseReport, PreAuditQuestion, QuoteRequest, QuoteRequestLocation, QuoteRequestScope,
    QuoteRequestStandard, SalesQuote, UserNote } from 'app/shared/models/domain/data.model';
import { AuditorsToStandardView } from './models/auditors-to-standard-view.model';
import { AuditDetailsView } from './audit-details/audit-details.types';
import { AuditDocumentationRequirementsView } from './models/audit-documentation-requirements-view.model';
import { AuditAttachmentsView } from './models/audit-attachments-view.model';
import { AuditBallotsView } from './models/audit-ballots-view.model';
import { AuditListView, CustomerAuditListView } from './models/audit-list-view.model';
import { AuditorHistoryView } from 'app/shared/models/view/auditor-history-view.model';
import { AuditStatusView } from './models/audit-status-view.model';
import { AuditEmailView } from './models/audit-email-view.model';
import { AuditNCRDocumentView } from 'app/shared/models/view/audit-ncr-document-view.model';
import { ClientToContactView } from '../clients/models/clients.types';
import { AuditReport, AuditReportView } from './models/audit-report-view.model';
import { AuditCertificateView } from 'app/shared/models/view/audit-certificate-view.model';
import { QuoteRequestLocationAuditView } from './models/quote-location-audit-view.model';
import { InvoiceView } from 'app/shared/models/view/invoice-view.model';
import { AuditsSameYearByAuditView, AuditTripView } from './models/audit-trips-view.model';
import { AuditProgramReviewView } from './models/audit-program-review-view.model';
import { StandardsToClientView } from 'app/shared/models/view/standards-to-client-view.model';
import moment from 'moment';
import { AuditTypePreLoadDocument } from '../documents/document-detail.types';
import { AuditorAuditProgramReviewView } from './models/auditor-audit-program-review-view.model';
import { AuditTripDetailView, AuditView } from 'app/shared/models/domain/data-view.model';
import { DataFormRequest } from 'app/shared/components/data-form/data-form.model';
import { AlertService } from 'app/shared/services/alert.service';
import { AuditBallotHistoryView } from 'app/shared/models/view/audit-ballot-history-view.model';
import { AuditRiskAssessmentHistoryView } from 'app/shared/models/view/audit-risk-assessment-history-view.model';
import { FiltersDbService } from 'app/core/service';

type PreAuditQuestionsScope = '' | 'public/';

type AuditAdditionalStandardItem = {
    deleted?: boolean;
    [key: string]: unknown;
};

type AuditDetailsBaseTaskResults = {
    audit: Audit;
    auditorsToAudit: AuditorsToAudit[];
    auditDocumentationRequirements: AuditDocumentationRequirement[] | null;
    preAuditQuestions: PreAuditQuestion[];
    quoteRequest: QuoteRequest | null;
    quoteRequestLocations: QuoteRequestLocation[] | null;
    client: Client | null;
    clientToContacts: ClientToContactView[] | null;
    applications: Application[] | null;
    quoteRequestStandards: QuoteRequestStandard[] | null;
    standardScopes: QuoteRequestScope[] | null;
    quoteRequestIafCode: ApplicationIndustryCode[] | null;
    quoteRequestLocationAuditViews: QuoteRequestLocationAuditView[];
};

type AuditDetailsAdminTaskResults = {
    clientAdditionalSites: ClientAdditionalSite[] | null;
    standardsToClient: StandardsToClientView[] | null;
    clientAdditionalIaf: ClientAdditionalIAFCode[] | null;
    notes: UserNote[];
    completedAudits: Audit[] | null;
    additionalAuditStandard: AuditAdditionalStandardItem[] | null;
};

type AuditDetailsTaskResults =
    | AuditDetailsBaseTaskResults
    | (AuditDetailsBaseTaskResults & AuditDetailsAdminTaskResults);

type AuditDetailsBaseTaskObservables = {
    audit: Observable<Audit>;
    auditorsToAudit: Observable<AuditorsToAudit[]>;
    auditDocumentationRequirements: Observable<AuditDocumentationRequirement[] | null>;
    preAuditQuestions: Observable<PreAuditQuestion[]>;
    quoteRequest: Observable<QuoteRequest | null>;
    quoteRequestLocations: Observable<QuoteRequestLocation[] | null>;
    client: Observable<Client | null>;
    clientToContacts: Observable<ClientToContactView[] | null>;
    applications: Observable<Application[] | null>;
    quoteRequestStandards: Observable<QuoteRequestStandard[] | null>;
    standardScopes: Observable<QuoteRequestScope[] | null>;
    quoteRequestIafCode: Observable<ApplicationIndustryCode[] | null>;
    quoteRequestLocationAuditViews: Observable<QuoteRequestLocationAuditView[]>;
};

type AuditDetailsAdminTaskObservables = {
    clientAdditionalSites: Observable<ClientAdditionalSite[] | null>;
    standardsToClient: Observable<StandardsToClientView[] | null>;
    clientAdditionalIaf: Observable<ClientAdditionalIAFCode[] | null>;
    notes: Observable<UserNote[]>;
    completedAudits: Observable<Audit[] | null>;
    additionalAuditStandard: Observable<AuditAdditionalStandardItem[] | null>;
};

type AuditDetailsTaskObservables =
    | AuditDetailsBaseTaskObservables
    | (AuditDetailsBaseTaskObservables & AuditDetailsAdminTaskObservables);

@Injectable({
    providedIn: 'root'
})
export class AuditsService {
    baseUrl = environment.apiBaseUrl;
    httpOptions: any = {
        withCredentials: true,
        observe: 'response' as const
    };
    public readonly schedulingBlockedTitle: string = 'Certificate Suspended';
    public readonly schedulingBlockedReason: string = 'Certificate for this standard is currently suspended. Audit scheduling cannot proceed.';
    private readonly requestKey: string = 'iapmo-audit-search-request';
    private readonly requestKeyRiskMatrix: string = 'iapmo-risk-matrix-search-request';
    private readonly requestCertificateKey: string = 'iapmo-audit-certificates-search-request';
    private readonly clientsRequestKey: string = 'iapmo-audits';
    private readonly _contact: BehaviorSubject<ClientSearchView | null> = new BehaviorSubject(null);
    private readonly _clients: BehaviorSubject<PageResponse<ClientSearchView> | null> = new BehaviorSubject(null);
    private readonly sortAuditAttachmentsMap: any = {
        uploadDate: 'uploadDate'
    };
    private readonly sortAuditBallotsMap: any = {
        openDate: 'openDate',
        closeDate: 'closeDate',
        decisionDate: 'decisionDate'
    };
    private readonly _audits: BehaviorSubject<PageResponse<AuditListView> | null> = new BehaviorSubject(null);
    private readonly _auditDetails: BehaviorSubject<AuditDetailsView | null> = new BehaviorSubject(null);
    private readonly _auditCertificates: BehaviorSubject<PageResponse<AuditCertificateView>> = new BehaviorSubject(null);
    private readonly _riskMatrix: BehaviorSubject<PageResponse<any>> = new BehaviorSubject(null);
    private readonly _viewAnswer: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private readonly _openConfirmed: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private readonly _quoteAttachmentUploaded: BehaviorSubject<DataFormRequest | null> = new BehaviorSubject(null);
    private readonly _renderTabForm: BehaviorSubject<DataFormRequest | null> = new BehaviorSubject(null);
    private readonly _saveAllYearsAudits: BehaviorSubject<boolean> = new BehaviorSubject(null);
    private readonly _updateAuditTab: BehaviorSubject<string | null> = new BehaviorSubject(null);
    private readonly _auditTrips: BehaviorSubject<PageResponse<AuditTripDetailView> | null> = new BehaviorSubject(null);
    private readonly _auditStatus: BehaviorSubject<PageResponse<AuditStatusView> | null> = new BehaviorSubject(null);
    private readonly _auditAuditor: BehaviorSubject<PageResponse<AuditorHistoryView> | null> = new BehaviorSubject(null);
    private readonly _auditEmail: BehaviorSubject<PageResponse<AuditEmailView> | null> = new BehaviorSubject(null);
    private readonly _auditBallot: BehaviorSubject<PageResponse<AuditBallotHistoryView> | null> = new BehaviorSubject(null);
    private readonly _auditRiskAssessment: BehaviorSubject<PageResponse<AuditRiskAssessmentHistoryView> | null> = new BehaviorSubject(null);

    constructor(
        private readonly _httpClient: HttpClient,
        private readonly alertService: AlertService,
        private readonly _datePipe: DatePipe,
        private readonly _filtersDbService: FiltersDbService
    ) {
    }

    get contact$(): Observable<ClientSearchView> {
        return this._contact.asObservable();
    }

    get clients$(): Observable<PageResponse<ClientSearchView>> {
        return this._clients.asObservable();
    }

    get audits$(): Observable<PageResponse<AuditListView>> {
        return this._audits.asObservable();
    }
    get auditDetails$(): Observable<AuditDetailsView | null> {
        return this._auditDetails.asObservable();
    }
    get auditCertificates$(): Observable<PageResponse<AuditCertificateView>> {
        return this._auditCertificates.asObservable();
    }
    get riskMatrix$(): Observable<PageResponse<any>> {
        return this._riskMatrix.asObservable();
    }

    get viewAnswer$(): Observable<boolean> {
        return this._viewAnswer.asObservable();
    }

    get auditStatus$(): Observable<PageResponse<AuditStatusView> | null> {
        return this._auditStatus.asObservable();
    }

    get auditAuditor$(): Observable<PageResponse<AuditorHistoryView> | null> {
        return this._auditAuditor.asObservable();
    }

    get auditEmail$(): Observable<PageResponse<AuditEmailView> | null> {
        return this._auditEmail.asObservable();
    }

    get auditBallot$(): Observable<PageResponse<AuditBallotHistoryView> | null> {
        return this._auditBallot.asObservable();
    }

    get auditRiskAssessment$(): Observable<PageResponse<AuditRiskAssessmentHistoryView> | null> {
        return this._auditRiskAssessment.asObservable();
    }

    setViewAnswer(value: boolean): void {
        this._viewAnswer.next(value);
    }

    get openConfirmed$(): Observable<boolean> {
        return this._openConfirmed.asObservable();
    }

    setOpenConfirmed(value: boolean): void {
        this._openConfirmed.next(value);
    }

    get quoteAttachmentUploaded$(): Observable<DataFormRequest | null> {
        return this._quoteAttachmentUploaded.asObservable();
    }

    setQuoteAttachmentUploaded(value: DataFormRequest | null): void {
        this._quoteAttachmentUploaded.next(value);
    }

    get renderTabForm$(): Observable<DataFormRequest | null> {
        return this._renderTabForm.asObservable();
    }

    get saveAllYearsAudits$(): Observable<boolean> {
        return this._saveAllYearsAudits.asObservable();
    }

    setRenderTabForm(value: DataFormRequest | null): void {
        this._renderTabForm.next(value);
    }

    setSaveAllYearsAudits(value: boolean): void {
        this._saveAllYearsAudits.next(value);
    }

    get updateAuditTab$(): Observable<string | null> {
        return this._updateAuditTab.asObservable();
    }

    setUpdateAuditTab(value: string | null): void {
        this._updateAuditTab.next(value);
    }

    get auditTrips$(): Observable<PageResponse<AuditTripDetailView> | null> {
        return this._auditTrips.asObservable();
    }

    getParams(request, params): any {
        params['itemsPerPage'] = request.itemsPerPage?.toString() ?? environment.defaultItemsPerPage.toString();
        params['currentPage'] = request.currentPage?.toString() ?? '1';

        if (request.sort && request.sortDir) {
            params[`sort[${request.sort}]`] = request.sortDir;
        }

        if (request.dateOption && request.dateOption != '' && request.dateOption == 'daysFromNow' && request.daysFromNow !== undefined) {
            const tempDate = new Date();
            const tempDate1 = moment(moment(tempDate, 'MM-DD').add(request.daysFromNow, 'days'), 'MM-DD').format('MM-DD');
            params['find[expireDay]'] = tempDate1;
        }

        if (request.dateOption && request.dateOption != '' && request.dateOption == 'byMonth' && request.byMonth !== undefined) {
            const tempDate = new Date().getFullYear();
            const tempDate1 = request.byMonth;
            params['find[expireMonth]'] = tempDate1;
            params['find[expireYear:gte]'] = tempDate;
        }

        if (request.dateOption && request.dateOption != '' && request.dateOption == 'byYear' && request.byYear !== undefined) {
            const tempDate1 = request.byYear;
            params['find[expireYear]'] = tempDate1;
        }

        if (request.auditorId && request.auditorId != '') {
            params['find[auditorId]'] = request.auditorId;
        }

        if (request.priorityClient && request.priorityClient != 2) {
            params['find[priorityClient]'] = request.priorityClient;
        } else if (request.priorityClient == 2) {
            params['find[priorityClient]'] = 0;
        }

        if (request.query) {
            params['find[auditId]'] = request.query;
        }

        if (request['auditTypeId']) {
            params['find[auditTypeId]'] = request['auditTypeId'];
        }

        if (request['standardId']) {
            params['find[standardId]'] = request['standardId'];
        }

        if (request.userId) {
            params['find[CreatedById]'] = request.userId;
        }

        if (request.attachmentType) {
            params['find[attachmentType]'] = request.attachmentType;
        }

        if (request.attachmentTypeNot) {
            params['find[attachmentType:neq]'] = request.attachmentTypeNot;
        }

        if (request.description) {
            params['orLike[description]'] = request.description;
        }

        if (request.documentType) {
            params['find[documentType]'] = request.documentType;
        }

        if (request['salesQuoteId']) {
            params['find[salesQuoteId]'] = request.salesQuoteId;
        }
        if (request['salesQuoteIds']) {
            params['find[salesQuoteId:in]'] = request.salesQuoteIds?.join();
        }

        if (request.clientId) {
            params['find[clientId]'] = request.clientId;
        }

        if (request.auditorId) {
            params['find[auditorId]'] = request.auditorId;
        }

        if (request['auditorNames']) {
            params['orLike[auditorNames]'] = request['auditorNames'];
        }

        if (request['companyName']) {
            params['orLike[companyName]'] = request['companyName'];
        }

        if (request['auditStatus']) {
            params['find[auditStatus]'] = request['auditStatus'];
        }

        if (request.documentName) {
            params['orlike[DocumentName]'] = request.documentName;
        }

        if (request.reportTypeId) {
            params['find[reportTypeId]'] = request.reportTypeId;
        }

        if (request.reportStatusList?.length) {
            params['find[reportStatusId:in]'] = request.reportStatusList.join();
        }

        if (request.createdDate) {
            const date = new Date(request.createdDate);
            const first = new Date(date.getFullYear(), date.getMonth(), 1);
            const firstDay = this._datePipe.transform(first, 'dd');
            const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            const lastDay = this._datePipe.transform(last, 'dd');
            params['find[createdDate:gte]'] = this._datePipe.transform(request.createdDate, 'yyyy-MM-' + firstDay);
            params['find[createdDate:lte]'] = this._datePipe.transform(request.createdDate, 'yyyy-MM-' + lastDay);
        }

        if (request.actualStartDate) {
            params['find[actualStartDate:gte]'] = this._datePipe.transform(request.actualStartDate, 'yyyy-MM-dd');
            params['find[actualStartDate:lte]'] = this._datePipe.transform(request.actualEndDate, 'yyyy-MM-dd');
        }

        if (request.queryStartDate) {
            const date = new Date(request.queryStartDate);
            const first = new Date(date.getFullYear(), date.getMonth(), 1);
            const firstDay = this._datePipe.transform(first, 'dd');
            const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
            const lastDay = this._datePipe.transform(last, 'dd');
            params['find[scheduledDate:gte]'] = this._datePipe.transform(request.queryStartDate, 'yyyy-MM-' + firstDay);
            params['find[scheduledDate:lte]'] = this._datePipe.transform(request.queryEndDate, 'yyyy-MM-' + lastDay);
        }

        if (request.auditorId) {
            params['auditorId'] = request.auditorId;
        }

        if (request.auditorIds) {
            params['find[auditorUserIds:split]'] = request.auditorIds
        }

        if (request.sponsorLocationId) {
            params['find[sponsor]'] = request.sponsorLocationId;
        }

        if (request.auditorId == 0) {
            params['auditorId'] = request.auditorId;
        }

        if (request.salesRepId) {
            params['salesRepId'] = request.salesRepId;
        }

        if (request.clientStatusId) {
            params['clientStatusId'] = request.clientStatusId;
        }

        if (request.industryCodeId) {
            params['industryCodeId'] = request.industryCodeId;
        }

        if (request.salesResponsibleId) {
            params['find[salesResponsibleId]'] = request.salesResponsibleId;
        }

        if (request.searchParam) {
            params['searchParam'] = request.searchParam;
        }

        if (request.sponsorLocationId) {
            params['sponsorLocationId'] = request.sponsorLocationId;
        }

        if (request.standardId) {
            params['standardId'] = request.standardId;
        }

        if (request.standardList && request.standardList?.length) {
            params['find[standardId:in]'] = request.standardList.join();
        }

        if (request.documentId && request.documentId?.length && request.documentIdNotIn) {
            params['find[documentId:nin]'] = request.documentId.join();
        }
        else if (request.documentId && request.documentId?.length) {
            params['find[documentId:in]'] = request.documentId.join();
        }

        if (request.auditStatusNotIn) {
            params['find[auditStatus:nin]'] = request.auditStatusNotIn.join();
        }

        if (request.auditStatusList && request.auditStatusList?.length) {
            params['find[auditStatusId:in]'] = request.auditStatusList.join();
        }

        if (request.auditTypeList && request.auditTypeList?.length) {
            params['find[auditTypeId:in]'] = request.auditTypeList.join();
        }

        if (request.clientStatusList && request.clientStatusList?.length) {
            params['find[clientStatusId:in]'] = request.clientStatusList.join();
        }

        if (request.sponsorLocationList && request.sponsorLocationList?.length) {
            params['find[sponsorLocationId:in]'] = request.sponsorLocationList.join();
        }

        if (request.auditCertificateStatusList && request.auditCertificateStatusList?.length) {
            params['find[certificateStatusId:in]'] = request.auditCertificateStatusList.join();
        }

        if (request.auditNumber) {
            params['find[auditNumber]'] = request.auditNumber;
        }

        if (request.recipientNumber) {
            params['like[auditNumber]'] = request.recipientNumber;
        }
        if (request.city) {
            params['like[city]'] = request.city;
        }
        if (request.postalCode) {
            params['like[postalCode]'] = request.postalCode;
        }
        if (request.stateProvinceId) {
            params['find[stateProvinceId]'] = request.stateProvinceId;
        }
        if (request.countryId) {
            params['find[countryId]'] = request.countryId;
        }

        if (request.auditIdOnly) {
            params['find[auditId]'] = request.auditIdOnly;
        }

        if (request['clientNameNumber']) {
            params['orLike[clientNumber]'] = request['clientNameNumber'];
            params['orLike[companyName]'] = request['clientNameNumber'];
        }
        if (request['certificateNumber']) {
            params['orLike[certificateNumber]'] = request['certificateNumber'];
        }

        if (request['industryCodeList'] && request['industryCodeList']?.length) {
            params['find[industryCodeId:in]'] = request['industryCodeList'].join();
        }

        if (request['auditIndustryCodeList'] && request['auditIndustryCodeList']?.length) {
            params['find[industryCodeId:in]'] = request['auditIndustryCodeList'].join();
        }

        if (request['auditStandardList'] && request['auditStandardList']?.length) {
            params['find[standardIdAuditedFor:in]'] = request['auditStandardList'].join();
        }

        if (request['additionalStandardList']?.length) {
            params['find[additionalStandardIds:split]'] = request['additionalStandardList'].join();
        }

        if (request['integratedAudit']) {
            params['find[integratedAudit]'] = 1;
        }

        if (request['country'] && request['country']?.length) {
            params['find[countryId:in]'] = request['country'].join();
        }

        if (request['auditCertificateStandardList'] && request['auditCertificateStandardList']?.length) {
            params['find[standardId:in]'] = request['auditCertificateStandardList'].join();
        }

        if (request['auditAuditorName']) {
            params['orLike[auditorNames]'] = request['auditAuditorName'];
        }

        if (request['auditAuditorNameOnly']) {
            params['orLike[auditorName]'] = request['auditAuditorNameOnly'];
        }

        if (request['auditReviewerName']) {
            params['orLike[reviewerName]'] = request['auditReviewerName'];
        }

        if (request['auditAuditorList'] && request['auditAuditorList']?.length) {
            params['find[auditorIds:split]'] = request['auditAuditorList'].join();
        }

        if (request['auditReviewerList'] && request['auditReviewerList']?.length) {
            params['find[reviewerId:in]'] = request['auditReviewerList'].join();
        }

        if (request['userId']) {
            // params['find[CreatedBy]'] = request['userId'];
            params['find[CreatedById]'] = request['userId'];
        }

        if (request['auditUserId']) {
            // params['find[CreatedBy]'] = request['userId'];
            params['find[QuoteRequestCreatedByUserId]'] = request['auditUserId'];
        }

        if (request['auditCertificationTypeList'] && request['auditCertificationTypeList']?.length) {
            params['find[CertificationTypeId:in]'] = request['auditCertificationTypeList'].join();
        }

        if (request.initialContactDateFrom) {
            params['find[initialContactDate:gte]'] = this._datePipe.transform(request.initialContactDateFrom, 'yyyy-MM-dd');
        }

        if (request.initialContactDateTo) {
            params['find[initialContactDate:lte]'] = this._datePipe.transform(request.initialContactDateTo, 'yyyy-MM-dd');
        }

        if (request.dropDeadDateFrom) {
            params['find[dropDeadDate:gte]'] = this._datePipe.transform(request.dropDeadDateFrom, 'yyyy-MM-dd');
        }

        if (request.dropDeadDateTo) {
            params['find[dropDeadDate:lte]'] = this._datePipe.transform(request.dropDeadDateTo, 'yyyy-MM-dd');
        }

        if (request.tentativeDateFrom) {
            params['find[tentativeDate:gte]'] = this._datePipe.transform(request.tentativeDateFrom, 'yyyy-MM-dd');
        }

        if (request.tentativeDateTo) {
            params['find[tentativeDate:lte]'] = this._datePipe.transform(request.tentativeDateTo, 'yyyy-MM-dd');
        }

        if (request.actualStartDateFrom) {
            params['find[actualStartDate:gte]'] = this._datePipe.transform(request.actualStartDateFrom, 'yyyy-MM-dd');
        }

        if (request.actualStartDateTo) {
            params['find[actualStartDate:lte]'] = this._datePipe.transform(request.actualStartDateTo, 'yyyy-MM-dd');
        }

        if (request.actualEndDateFrom) {
            params['find[actualEndDate:gte]'] = this._datePipe.transform(request.actualEndDateFrom, 'yyyy-MM-dd');
        }

        if (request.actualEndDateTo) {
            params['find[actualEndDate:lte]'] = this._datePipe.transform(request.actualEndDateTo, 'yyyy-MM-dd');
        }

        if (request.ballotDateFrom) {
            params['find[ballotDate:gte]'] = this._datePipe.transform(request.ballotDateFrom, 'yyyy-MM-dd');
        }

        if (request.ballotDateTo) {
            params['find[ballotDate:lte]'] = this._datePipe.transform(request.ballotDateTo, 'yyyy-MM-dd');
        }

        if (request.issueDateFrom) {
            params['find[ballotDate:gte]'] = this._datePipe.transform(request.issueDateFrom, 'yyyy-MM-dd');
        }

        if (request.issueDateTo) {
            params['find[ballotDate:lte]'] = this._datePipe.transform(request.issueDateTo, 'yyyy-MM-dd');
        }

        if (request.expiryDateFrom) {
            params['find[expiryDate:gte]'] = this._datePipe.transform(request.expiryDateFrom, 'yyyy-MM-dd');
        }

        if (request.expiryDateTo) {
            params['find[expiryDate:lte]'] = this._datePipe.transform(request.expiryDateTo, 'yyyy-MM-dd');
        }

        if (request.deleted == false) {
            params['find[deleteFlag]'] = 0;
        }

        if (request.type && request.type == 'admin') {
            params['find[type]'] = 0;
        }

        if (request.type && request.type == 'auditor') {
            params['find[type]'] = 1;
        }

        if (request.isExport) {
            params['fileType'] = request.exportType;
            params['excludedColumns'] = request.hideColumns;
        }

        Object.keys(params).forEach((key) => {
            if (params[key] === null || params[key] === '') {
                delete params[key];
            }
        });

        return params;
    }

    getAudits(request: PageRequest): Observable<PageResponse<AuditListView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditListView>>(`${this.baseUrl}api/data/audit-list-view`, { params: params })
            .pipe(map((audits: any) => {
                const response: PageResponse<AuditListView> = new PageResponse<AuditListView>();
                response.items = audits.items;
                response.totalItems = audits.page.totalItems;
                response.currentPage = audits.page.currentPage;
                response.itemsPerPage = audits.page.itemsPerPage;
                response.query = null;
                response.page = audits?.page;
                this._audits.next(response);
                return response;
            }));
    }

    getCustomerAudits(request: PageRequest): Observable<PageResponse<CustomerAuditListView>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<CustomerAuditListView>>(`${this.baseUrl}api/data/customer/audit-list-view`, { params: params })
            .pipe(map((audits: any) => {
                const response: PageResponse<CustomerAuditListView> = new PageResponse<CustomerAuditListView>();
                response.items = audits.items.map((rows: any) => {
                    return {
                        'clientName': rows.clientName,
                        'auditStatus': rows.auditStatus,
                        'auditNumber': rows.auditNumber,
                        'auditId': rows.auditNumber,
                        'clientId': rows.clientId,
                        'auditType': rows.auditType,
                        'dropDeadDate': rows.dropDeadDate,
                        'startDate': rows.startDate,
                        'endDate': rows.endDate
                    };
                });
                response.totalItems = audits.page.totalItems;
                response.currentPage = audits.page.currentPage;
                response.itemsPerPage = audits.page.itemsPerPage;
                response.query = null;
                response.page = audits?.page;
                return response;
            }));
    }

    searchAuditNumbers(query: string): Observable<string[]> {
        const params = {
            'orLike[AuditNumber]': query,
            'itemsPerPage': 10
        };

        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit-list-view`, { params: params })
            .pipe(
                map((audits: any) => {
                    return audits.items;
                })
            );
    }

    getAuditsData(request: AuditsSearchRequest, path: any, exportFile: boolean): Observable<any> {
        const urlSegment = 'audit-view';
        return this.getAuditDataDetails(request, path, exportFile, urlSegment);
    }

    getAuditTripsData(request: AuditsSearchRequest, path: any, exportFile: boolean): Observable<any> {
        const urlSegment = 'audit-trip-detail-view';
        return this.getAuditDataDetails(request, path, exportFile, urlSegment);
    }

    getAuditDataDetails(request: AuditsSearchRequest, path: any, exportFile: boolean, urlSegment: string): Observable<any> {
        let params = {};
        path = path ? '/' + path : '';
        let url = '';
        if (request['auditorIds']) {
            url = `api/data/auditor/${urlSegment}` + path;
        } else {
            url = `api/data/${urlSegment}` + path;
        }
        params = this.getParams(request, params);

        if (exportFile) {
            url += '/export';
            return this._httpClient.get(this.baseUrl + url, { params: params, responseType: 'arraybuffer' });
        }

        params = this.serialize(params);
        return this._httpClient.get<any>(this.baseUrl + url + '?' + params, params).pipe(
            map((clients: any) => {
                const returnData = clients;
                const ret: PageResponse<any> = new PageResponse<any>();
                ret.items = returnData.items.map((row) => {
                    // return row as any;
                    return {
                        'auditNumber': row.auditId,
                        'auditId': row.auditId,
                        'auditType': row['auditType'],
                        'companyName': row.companyName,
                        'auditStatus': row.auditStatus,
                        'clientNumber': row.clientNumber,
                        'initialContactDate': row.initialContactDate,
                        'tentativeDate': row.tentativeDate,
                        'dropDeadDate': row.dropDeadDate,
                        'totalManDays': row?.totalManDaysAll > 0 ? row?.totalManDaysAll : row?.totalManDays,
                        'onSite': row?.onSiteAll > 0 ? row?.onSiteAll : row?.onSite,
                        'offSite': row?.offSiteAll > 0 ? row?.offSiteAll : row?.offSite,
                        ...row
                    } as any;
                });
                ret.totalItems = returnData?.page?.totalItems;
                ret.itemsPerPage = returnData?.page?.itemsPerPage || request.itemsPerPage;
                ret.currentPage = request.currentPage;
                ret.page = returnData?.page;
                this._clients.next(ret);
                this._audits.next(ret);
                this._auditTrips.next(ret);
                if (request.storeLocalStorage) {
                    this.saveLocalRequest(this.requestKey, request).subscribe();
                    this.saveLocalRequest(this.clientsRequestKey, ret).subscribe();
                }
                return ret;
            })
        );
    }

    getRiskMatrixData(request: AuditsSearchRequest, path: any, exportFile: boolean): Observable<any> {
        let params = {};
        path = path ? '/' + path : '';
        let url = 'api/data/risk-assessment-view' + path;
        params = this.getParams(request, params);
        if (exportFile) {
            url += '/export';
            return this._httpClient.get(this.baseUrl + url, { params: params, responseType: 'arraybuffer' });
        }
        params = this.serialize(params);
        return this._httpClient.get<any>(this.baseUrl + url + '?' + params, params).pipe(
            map((clients: any) => {
                const returnData = clients;
                const ret: PageResponse<ClientSearchView> = new PageResponse<any>();
                ret.items = returnData.items.map((row) => {
                    return {
                        'auditId': row.auditId,
                        'auditType': row['auditType'],
                        'companyName': row.companyName,
                        'standard': row.standard,
                        'siteName': row.standard,
                        'address': row.address,
                        'auditorName': row.auditorName,
                        'auditStatus': row.auditStatus,
                        ...row
                    } as any;
                });
                ret.totalItems = returnData?.page?.totalItems;
                ret.currentPage = request.currentPage;
                ret.itemsPerPage = request.itemsPerPage;
                ret.page = clients.page;
                this._clients.next(ret);
                this.saveLocalRequest(this.requestKey, request).subscribe();
                this.saveLocalRequest(this.clientsRequestKey, ret).subscribe();
                this._riskMatrix.next(ret);
                return ret;
            })
        );
    }

    getSalesQuoteBySalesQuoteId(salesQuoteId: string): Observable<SalesQuote> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote/${salesQuoteId}`)
            .pipe(map((salesQuote: any) => {
                return salesQuote.item as SalesQuote;
            })
            );
    }

    getAuditorsByStandardId(standardId: string | string[], sponsorLocationId: number = null, industryCodeId: number = null): Observable<PageResponse<AuditorsToStandardView>> {
        const params = {
            'sort[AuditorName]': 'ASC',
            'itemsPerPage': 250,
        };

        if (industryCodeId && industryCodeId > 0) {
            params['find[IndustryCodeIds:split]'] = industryCodeId;
        }

        if (Array.isArray(standardId)) {
            params['find[StandardId:in]'] = standardId.join();
        } else {
            params['find[StandardId]'] = standardId;
        }

        if (sponsorLocationId >= 4) {
            params['find[SponsorLocationId]'] = sponsorLocationId;
        }

        return this._httpClient.get<any>(`${this.baseUrl}api/data/auditors-to-standard-view`, { params: params })
            .pipe(map((auditors: any) => {
                const response: PageResponse<AuditorsToStandardView> = new PageResponse<AuditorsToStandardView>();
                response.items = auditors.items;
                response.totalItems = auditors.page.totalItems;
                response.currentPage = auditors.page.currentPage;
                response.itemsPerPage = auditors.page.itemsPerPage;
                response.query = null;
                return response;
            })
            );
    }

    saveAuditData(auditId: number, audit: Audit, isPublic: string = ''): Observable<PageResponse<Audit>> {
        if (auditId) {
            return this._httpClient.put<PageResponse<Audit>>(`${this.baseUrl}api/data/${isPublic}audit/${auditId}`, audit);
        } else {
            return this._httpClient.post<PageResponse<Audit>>(`${this.baseUrl}api/data/${isPublic}audit`, audit);
        }
    }

    saveAdditionalAuditStandardData(id = null, payload): Observable<PageResponse<any>> {
        payload.standardId = parseInt(payload.standardId);
        if (payload['standardId'] != '' || payload['standardId'] == 0) {
            if (id && payload['standardId'] == 0) {
                return this._httpClient.delete<any>(`${this.baseUrl}api/data/audit-additional-standard/${id}`, {});
            }
            else if (id && payload.standardId != 0) {
                return this._httpClient.put<PageResponse<any>>(`${this.baseUrl}api/data/audit-additional-standard/${id}`, payload);
            } else if (payload.standardId != 0) {
                return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit-additional-standard`, payload);
            }
        }
        return of(null);
    }

    saveAuditQuoteRequestLocationData(id = null, payload): Observable<PageResponse<any>> {
        if (payload['quoteRequestLocationId'] != '' || payload['quoteRequestLocationId'] == 0) {
            if (id && payload['quoteRequestLocationId'] == 0) {
                return this._httpClient.delete<any>(`${this.baseUrl}api/data/audit-quote-request-location/${id}`, {});
            }
            else if (id) {
                return this._httpClient.put<PageResponse<any>>(`${this.baseUrl}api/data/audit-quote-request-location/${id}`, payload);
            } else {
                return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit-quote-request-location`, payload);
            }
        }
        return of(null);
    }

    updateRequiredDocuments(id: number, data): Observable<PageResponse<any>> {
        return this._httpClient.put<PageResponse<any>>(`${this.baseUrl}api/data/audit-documentation-requirements/${id}`, data);
    }

    sendActionItems(id: number): Observable<PageResponse<boolean>> {
        return this._httpClient.post<PageResponse<boolean>>(`${this.baseUrl}api/data/audit/${id}/action-items`, {});
    }

    saveAudit(form: any, audit: Audit, auditDetails: AuditDetailsView): any {

        return this.saveAuditData(auditDetails?.audit?.auditId, audit).pipe(switchMap((response) => {
            const auditorsToAuditDelta = {
                add: []
                , delete: []
            };

            let auditorsToAuditTasks: any[] = [];

            if (form?.auditorIds?.length) {
                for (var x: number = 0; x < form.auditorIds.length; x++) {
                    const toAdd: boolean = !auditDetails?.auditorsToAudit?.length || auditDetails?.auditorsToAudit?.findIndex(c => c.auditorId == form.auditorIds[x]) == -1;
                    if (true == toAdd) {
                        auditorsToAuditDelta.add.push(form.auditorIds[x])
                    }
                }
            }

            if (form?.auditorIds && auditDetails?.auditorsToAudit?.length) {
                for (var x: number = 0; x < auditDetails?.auditorsToAudit.length; x++) {
                    const toRemove: boolean = form.auditorIds.indexOf(auditDetails?.auditorsToAudit[x].auditorId) == -1;
                    if (true == toRemove) {
                        auditorsToAuditDelta.delete.push(auditDetails?.auditorsToAudit[x].id)
                    }
                }
            }

            if (auditDetails?.auditorsToAudit?.length) {
                const auditor = auditDetails?.auditorsToAudit?.find(a => a.auditorId === form.leadAuditorId);
                if (auditor && !auditor.leadAuditor) {
                    auditor.leadAuditor = true;
                    this._httpClient.put<PageResponse<any>>(`${this.baseUrl}api/data/auditors-to-audit/${auditor.id}`, auditor).subscribe();
                }
            }

            if (auditorsToAuditDelta.add.length) {
                auditorsToAuditTasks = auditorsToAuditTasks.concat(auditorsToAuditDelta.add.map(auditorId =>
                    this.createAuditorToAudit(response.item.auditId, Number(auditorId), form.leadAuditorId === auditorId)))
            }

            if (auditorsToAuditDelta.delete.length) {
                auditorsToAuditTasks = auditorsToAuditTasks.concat(auditorsToAuditDelta.delete.map((ID) => {
                    const payload = {}
                    return this._httpClient.delete<any>(`${this.baseUrl}api/data/auditors-to-audit/${ID}`, payload);
                }))
            }

            // initialContactDate && dueDate required to schedule Pending Audit Reminder email
            if (response.item.auditStatusId == 1 && response.item.initialContactDate && response.item.dueDate) {
                const auditEmailSchedule = {
                    active: true,
                    auditId: response.item.auditId,
                    emailFireDate: new Date().toISOString(),
                } as AuditEmailSchedule;
                // schedule reminder email
                auditorsToAuditTasks = auditorsToAuditTasks.concat(
                    this._httpClient.post<any>(`${this.baseUrl}api/data/audit-email-schedule/schedule-pending-reminder`, auditEmailSchedule));
            }

            if (auditorsToAuditTasks?.length) {
                return forkJoin(auditorsToAuditTasks);
            }
            return of(null);
        }))

    }

    returnAuditToClient(returnToclientRequest: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit/return-audit-with-note`, returnToclientRequest);
    }

    readyForScheduling(returnToclientRequest: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit/ready-for-scheduling`, returnToclientRequest);
    }

    readyForRescheduling(request: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/workflow/audit/ready-for-rescheduling`, request);
    }

    updateAuditStatus(request: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit/update-status`, request);
    }

    createYearWiseAudits(request: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit/create-year-audits`, request);
    }

    updateSalesQuoteDetails(request: any): any {
        return this._httpClient.post<PageResponse<Audit>>(`${this.baseUrl}api/data/audit/update-sales-quote`, request);
    }

    addReportForAuditor(request: AuditReport): any {
        return this._httpClient.post<PageResponse<AuditReport>>(`${this.baseUrl}api/data/auditor/audit-report`, request);
    }

    createNextYearsAudits(request: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/audit/create-next-year-audits`, request);
    }
    generateAllYearsAudits(request: any): any {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/data/sales-quote/generate-all-year-audits`, request)
            .pipe(map((audits) => {
                this.setSaveAllYearsAudits(true);
                return audits;
            }));
    }

    resetAuditStatus(auditId: number, salesQuoteId: string): any {
        const payload = {
        }

        const params = {
            'find[Id]': salesQuoteId,
            'itemsPerPage': 1
        };

        return forkJoin([
            this._httpClient.put<PageResponse<any>>(`${this.baseUrl}api/workflow/audit/reset-audits-status/${auditId}`, payload),
        ]).pipe(switchMap(() => {
            return this._httpClient.get<any>(`${this.baseUrl}api/data/sales-quote`, { params: params })
                .pipe(map((response: any) => {
                    return response.items[0];
                }));
        }));
    }

    getIAFCodesByApplicationIdPublic(id: any): Observable<any> {
        id = id?.toString();
        if (id) {
            return this._httpClient.get<any>(this.baseUrl + 'api/data/public/application-industry-code?itemsPerPage=1000&find[quoteRequestId]=' + id)
                .pipe(map(data => data.items));
        }
        return of([]);
    }

    getAuditDetails(auditId: number): Observable<AuditDetailsView> {
        return this.getAuditDetailsByRole(auditId, false);
    }

    getAuditorAuditDetails(auditId: number): Observable<AuditDetailsView> {
        return this.getAuditDetailsByRole(auditId, true);
    }

    private getAuditDetailsByRole(auditId: number, isAuditor: boolean): Observable<AuditDetailsView> {
        const auditViewUrl = isAuditor
            ? `${this.baseUrl}api/data/auditor/audit-view/${auditId}?find[auditId]=${auditId}`
            : `${this.baseUrl}api/data/audit-view/${auditId}`;

        return this._httpClient.get<PageResponse<AuditView>>(auditViewUrl).pipe(
            switchMap((auditViewResponse: PageResponse<AuditView>) => {
                const tasks = this.buildAuditDetailsTasks(auditId, auditViewResponse.item, isAuditor);
                return forkJoin(tasks).pipe(
                    map((resultSet: AuditDetailsTaskResults) => this.mapAuditDetailsResponse(auditViewResponse, resultSet, isAuditor))
                );
            })
        );
    }

    private buildAdminAuditDetailsTasks(auditId: number, auditViewItem: AuditView): AuditDetailsAdminTaskObservables {
        const clientAdditionalSitesUrl = `${this.baseUrl}api/data/admin/client-additional-site?itemsPerPage=100&find[ClientId]=${auditViewItem?.clientId}`;
        return {
            clientAdditionalSites: auditViewItem?.clientId
            ? this._httpClient
                .get<PageResponse<ClientAdditionalSite>>(clientAdditionalSitesUrl)
                .pipe(map(response => response.items))
            : of<ClientAdditionalSite[]>([]),
            standardsToClient: (auditViewItem?.clientId) ? this._httpClient.get<PageResponse<StandardsToClientView>>(`${this.baseUrl}api/data/standards-to-client/view?itemsPerPage=100&find[ClientId]=${auditViewItem.clientId}`).pipe(map(response => response.items)) : of(null),
            clientAdditionalIaf: (auditViewItem?.clientId) ? this._httpClient.get<PageResponse<ClientAdditionalIAFCode>>(`${this.baseUrl}api/data/client-industry-code?itemsPerPage=100&find[ClientId]=${auditViewItem.clientId}`).pipe(map(response => response.items)) : of(null),
            notes: this._httpClient.get<PageResponse<UserNote>>(`${this.baseUrl}api/data/user-notes-view?itemsPerPage=1000&find[SubjectId]=${auditId.toString()}&find[SubjectType]=${'Audit'}`).pipe(map(response => response.items)),
            completedAudits: (auditViewItem?.clientId) ? this._httpClient.get<PageResponse<Audit>>(`${this.baseUrl}api/data/audit-view?itemsPerPage=10000&find[ClientId]=${auditViewItem.clientId}&find[AuditStatus]=Complete`).pipe(map(response => response.items)) : of(null),
            additionalAuditStandard: (auditViewItem?.auditId) ? this._httpClient.get<PageResponse<AuditAdditionalStandardItem>>(`${this.baseUrl}api/data/audit-additional-standard?itemsPerPage=1000&find[AuditId]=${auditViewItem.auditId}`).pipe(map(response => response.items)) : of(null),
        };
    }

    private buildAuditDetailsTasks(auditId: number, auditViewItem: AuditView, isAuditor: boolean): AuditDetailsTaskObservables {
        const tasks: AuditDetailsBaseTaskObservables = {
            audit: this._httpClient.get<PageResponse<Audit>>(`${this.baseUrl}api/data/audit/${auditId}`).pipe(map(response => response.item)),
            auditorsToAudit: this._httpClient.get<PageResponse<AuditorsToAudit>>(`${this.baseUrl}api/data/auditors-to-audit`, { params: { 'itemsPerPage': 50, 'find[AuditId]': auditId } }).pipe(map(response => response.items)),
            auditDocumentationRequirements: (auditViewItem?.standardIdAuditedFor)
                ? this._httpClient.get<PageResponse<AuditDocumentationRequirement>>(`${this.baseUrl}api/data/audit-documentation-requirements`, {
                    params: { 'itemsPerPage': 1, 'find[StandardId]': auditViewItem.standardIdAuditedFor, 'find[AuditTypeId]': auditViewItem.auditTypeId }
                }).pipe(map(response => response.items))
                : of(null),
            preAuditQuestions: this._httpClient.get<PageResponse<PreAuditQuestion>>(`${this.baseUrl}api/data/pre-audit-questions`, { params: { 'itemsPerPage': 1, 'find[AuditId]': auditId } }).pipe(map(response => response.items)),
            quoteRequest: (auditViewItem?.quoteRequestId) ? this.getQuoteRequestById(auditViewItem.quoteRequestId) : of(null),
            quoteRequestLocations: (auditViewItem?.quoteRequestId)
                ? this._httpClient.get<PageResponse<QuoteRequestLocation>>(`${this.baseUrl}api/data/quote-request-location?find[QuoteRequestId]=${auditViewItem.quoteRequestId}&sort[Created]=asc&itemsPerPage=1000`).pipe(map(response => response.items))
                : of(null),
            client: (auditViewItem?.clientId) ? this._httpClient.get<PageResponse<Client>>(`${this.baseUrl}api/data/admin/clients/${auditViewItem.clientId}`).pipe(map(response => response.item)) : of(null),
            clientToContacts: (auditViewItem?.clientId) ? this._httpClient.get<PageResponse<ClientToContactView>>(`${this.baseUrl}api/data/view/client-contact?find[ClientId]=${auditViewItem.clientId}`).pipe(map(response => response?.items)) : of(null),
            applications: (auditViewItem?.clientId) ? this._httpClient.get<PageResponse<Application>>(`${this.baseUrl}api/data/application?find[ClientId]=${auditViewItem.clientId}&sort[CreatedDate]=desc`).pipe(map(response => response.items)) : of(null),
            quoteRequestStandards: (auditViewItem?.quoteRequestId) ? this._httpClient.get<PageResponse<QuoteRequestStandard>>(`${this.baseUrl}api/data/quote-request-standard?find[QuoteRequestId]=${auditViewItem.quoteRequestId}&sort[Created]=asc`).pipe(map(response => response.items)) : of(null),
            standardScopes: (auditViewItem?.quoteRequestId) ? this._httpClient.get<PageResponse<QuoteRequestScope>>(`${this.baseUrl}api/data/quote-request-scope?find[QuoteRequestId]=${auditViewItem.quoteRequestId}&sort[Created]=asc&itemsPerPage=1000`).pipe(map(response => response.items)) : of(null),
            quoteRequestIafCode: (auditViewItem?.quoteRequestId) ? this.getIAFCodesByApplicationIdPublic(auditViewItem.quoteRequestId) : of(null),
            quoteRequestLocationAuditViews: this._httpClient.get<PageResponse<QuoteRequestLocationAuditView>>(`${this.baseUrl}api/data/quote-request-location-audit-view`, { params: { 'itemsPerPage': 100, 'find[AuditId]': auditId } }).pipe(map(response => response.items))
        };

        if (!isAuditor) {
            return {
                ...tasks,
                ...this.buildAdminAuditDetailsTasks(auditId, auditViewItem)
            };
        }

        return tasks;
    }

    private mapAuditDetailsResponse(auditViewResponse: PageResponse<AuditView>, resultSet: AuditDetailsTaskResults, isAuditor: boolean): AuditDetailsView {
        const {
            audit,
            auditorsToAudit,
            auditDocumentationRequirements,
            preAuditQuestions,
            quoteRequest,
            quoteRequestLocations,
            client,
            clientToContacts,
            applications,
            quoteRequestStandards,
            standardScopes,
            quoteRequestIafCode,
            quoteRequestLocationAuditViews
        } = resultSet;

        const requirements = this.mapToRequirementsView(auditDocumentationRequirements);
        const mainSite = quoteRequestLocations?.find(x => x.isMainSite);
        const otherSites = quoteRequestLocations?.filter(x => !x.isMainSite);

        const returnData = {
            auditView: auditViewResponse.item,
            audit: audit,
            auditorsToAudit: auditorsToAudit,
            auditDocumentationRequirements: requirements,
            preAuditQuestions: preAuditQuestions?.[0],
            quoteRequest: quoteRequest,
            quoteRequestLocations: quoteRequestLocations,
            mainSite: mainSite,
            otherSites: otherSites,
            quoteRequestStandards: quoteRequestStandards,
            standardScopes: standardScopes,
            quoteRequestIafCode: quoteRequestIafCode as ApplicationIndustryCode[],
            client: client,
            clientToContactsView: clientToContacts,
            applications: applications,
            quoteRequestLocationAuditViews: quoteRequestLocationAuditViews
        } as AuditDetailsView;

        if (!isAuditor) {
            const {
                clientAdditionalSites,
                standardsToClient,
                clientAdditionalIaf,
                notes,
                completedAudits,
                additionalAuditStandard
            } = resultSet as AuditDetailsBaseTaskResults & AuditDetailsAdminTaskResults;

            returnData.clientAdditionalSites = clientAdditionalSites;
            returnData.clientAdditionalIaf = clientAdditionalIaf;
            returnData.standardsToClient = standardsToClient as StandardsToClientView[];
            returnData.notes = notes;
            returnData.completedAudits = completedAudits;
            returnData.additionalAuditStandard = additionalAuditStandard?.filter(rows => !rows.deleted);
        }

        this._auditDetails.next(returnData);
        return returnData;
    }

    getAuditAttachments(request: PageRequest): Observable<PageResponse<AuditAttachmentsView>> {
        let params = {};
        params = this.getParams(request, params);
        if (request.sort && request.sortDir) {
            if (Object.keys(this.sortAuditAttachmentsMap).indexOf(request.sort) > -1) {
                params[`sort[${this.sortAuditAttachmentsMap[request.sort]}]`] = request.sortDir;
            }
            else {
                params[`sort[${request.sort}]`] = request.sortDir;
            }
        }

        return this._httpClient.get<PageResponse<AuditAttachmentsView>>(`${this.baseUrl}api/data/audit-attachments-view`, { params: params })
            .pipe(map((auditAttachments: any) => {
                const response: PageResponse<AuditAttachmentsView> = new PageResponse<AuditAttachmentsView>();
                response.items = auditAttachments.items?.map((rows: any) => {
                    return {
                        attachment: rows?.attachment,
                        ...rows
                    };
                });
                response.totalItems = auditAttachments.page.totalItems;
                response.currentPage = auditAttachments.page.currentPage;
                response.itemsPerPage = auditAttachments.page.itemsPerPage;
                response.query = null;
                return response;
            }));
    }

    getAttachment(ID: number): Observable<PageResponse<AuditAttachment>> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit-attachment/${ID}`)
            .pipe(map((attachment: any) => {
                const ret = new PageResponse<AuditAttachment>();
                ret.item = attachment.item;
                return ret;
            })
            );
    }

    removeAttachment(ID: number): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/audit-attachment/${ID}`);
    }

    getAuditBallots(request: PageRequest): Observable<PageResponse<AuditBallotsView>> {
        let params = {};
        params = this.getParams(request, params);
        if (request.sort && request.sortDir) {
            if (Object.keys(this.sortAuditBallotsMap).indexOf(request.sort) > -1) {
                params[`sort[${this.sortAuditBallotsMap[request.sort]}]`] = request.sortDir;
            }
            else {
                params[`sort[${request.sort}]`] = request.sortDir;
            }
        }

        return this._httpClient.get<PageResponse<AuditAttachmentsView>>(`${this.baseUrl}api/data/audit-ballots-view`, { params: params })
            .pipe(map((auditBallots: any) => {
                const response: PageResponse<AuditBallotsView> = new PageResponse<AuditBallotsView>();
                response.items = auditBallots.items;
                response.totalItems = auditBallots.page.totalItems;
                response.currentPage = auditBallots.page.currentPage;
                response.itemsPerPage = auditBallots.page.itemsPerPage;
                response.query = null;
                return response;
            }));
    }

    getBallot(ballotId: number): Observable<PageResponse<Ballot>> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit-ballot-detail/${ballotId}`)
            .pipe(map((ballot: any) => {
                const ret = new PageResponse<Ballot>();
                ret.item = ballot.item;
                return ret;
            })
            );
    }

    removeBallot(ballotId: number): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/audit-ballot-detail/${ballotId}`);
    }

    getQuoteRequestById(id: string): Observable<QuoteRequest> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/quote-request/' + id)
            .pipe(map((data) => {
                return data.item as QuoteRequest;
            }));
    }

    getExpenseReportById(id: string): Observable<ExpenseReport> {
        return this._httpClient.get<any>(this.baseUrl + 'api/auditor/expense-reports/' + id)
            .pipe(map((data) => {
                return data.item as ExpenseReport;
            }));
    }

    updateExpenseReport(expenseReport): Observable<ExpenseReport> {
        return this._httpClient.put<any>(this.baseUrl + 'api/auditor/expense-reports/' + expenseReport.id, expenseReport)
            .pipe(map((data) => {
                return data.item as ExpenseReport;
            }));
    }

    getAuditReportByExpenseReportId(id: string): Observable<AuditReport> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/audit-report?find[expenseReportId]=' + id)
            .pipe(map((data: any) => data.items[0] ?? {} as AuditReport));
    }

    createAuditorToAudit(auditId: number, auditorId: number, leadAuditor: boolean): Observable<PageResponse<AuditorsToAudit>> {
        const payload: Partial<AuditorsToAudit> = {
            auditId,
            auditorId: Number(auditorId),
            leadAuditor
        };
        return this._httpClient.post<PageResponse<AuditorsToAudit>>(`${this.baseUrl}api/data/auditors-to-audit`, payload);
    }

    getAuditBySalesQuoteId(salesQuoteId: string): Observable<Audit> {
        const params = {
            'find[SalesQuoteId]': salesQuoteId,
            'itemsPerPage': 1
        };

        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit`, { params: params })
            .pipe(map((response: any) => {
                return response.items[0];
            })
            );
    }

    getAuditsBySalesQuoteId(salesQuoteId: string): Observable<Audit[]> {
        const params = {
            'find[SalesQuoteId]': salesQuoteId,
            'itemsPerPage': 100
        };

        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit`, { params: params })
            .pipe(map((response: any) => {
                return response.items;
            })
            );
    }

    getAuditBySalesQuoteLineItemId(salesQuoteLineItemId: string): Observable<Audit> {
        const params = {
            'find[SalesQuoteLineItemId]': salesQuoteLineItemId,
            'itemsPerPage': 1
        };

        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit`, { params: params })
            .pipe(map((response: any) => {
                return response.items[0];
            })
            );
    }

    getAuditsBySalesQuoteLineItemId(salesQuoteLineItemId: string): Observable<Audit[]> {
        const params = {
            'find[SalesQuoteLineItemId]': salesQuoteLineItemId,
            'itemsPerPage': 100
        };

        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit`, { params: params })
            .pipe(map((response: any) => {
                return response.items;
            })
            );
    }

    getAuditorHistory(request: PageRequest): Observable<PageResponse<AuditorHistoryView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditorHistoryView>>(`${this.baseUrl}api/data/auditor-history-view`, { params: params })
            .pipe(map((auditorHistory: any) => {
                const response: PageResponse<AuditorHistoryView> = new PageResponse<AuditorHistoryView>();
                response.items = auditorHistory.items;
                response.totalItems = auditorHistory.page.totalItems;
                response.currentPage = auditorHistory.page.currentPage;
                response.itemsPerPage = auditorHistory.page.itemsPerPage;
                response.query = null;
                this._auditAuditor.next(response);
                return response;
            }));
    }

    getIndividualAuditorHistory(id: number): Observable<PageResponse<AuditorHistory>> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/auditor-history/${id}`)
            .pipe(map((auditorHistory: any) => {
                const ret = new PageResponse<AuditorHistory>();
                ret.item = auditorHistory.item;
                return ret;
            })
            );
    }

    removeAuditorHistory(Id: number): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/auditor-history/${Id}`);
    }

    getAuditStatusHistory(request: PageRequest): Observable<PageResponse<AuditStatusView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditStatusView>>(`${this.baseUrl}api/data/audit-status-view`, { params: params })
            .pipe(map((auditStatus: any) => {
                const response: PageResponse<AuditStatusView> = new PageResponse<AuditStatusView>();
                response.items = auditStatus.items;
                response.totalItems = auditStatus.page.totalItems;
                response.currentPage = auditStatus.page.currentPage;
                response.itemsPerPage = auditStatus.page.itemsPerPage;
                response.query = null;
                this._auditStatus.next(response);
                return response;
            }));
    }

    getAuditEmailHistory(request: any): Observable<PageResponse<AuditEmailView>> {
        let params = {};
        params = this.getParams(request, params);
        const httpOptionsLocal = { params: params };

        return forkJoin([
            this._httpClient.get<any>(this.baseUrl + 'api/data/email-history', { params: params })
        ])
            .pipe(
                map(([emails]: [any]) => {
                    const ret: PageResponse<AuditEmailView> = new PageResponse<AuditEmailView>();
                    ret.items = emails.items.map((row: EmailHistory) => {
                        return {
                            id: row.id,
                            auditId: row.auditId,
                            emailedDate: row.emailedDate,
                            jobId: row.hangfireJobId,
                            recipients: row.emailRecipients,
                            emailRecipients: row.emailRecipients,
                            sentBy: row.sentBy,
                            subject: row.subject,
                            emailSuccess: row.emailSuccess
                        } as AuditEmailView;
                    });
                    ret.totalItems = emails.page?.totalItems;
                    ret.currentPage = emails.page?.currentPage;
                    ret.itemsPerPage = emails.page?.itemsPerPage;
                    this._auditEmail.next(ret);
                    return ret;
                })
            );
    }

    getAuditNCRDocuments(request: PageRequest): Observable<PageResponse<AuditNCRDocumentView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditNCRDocumentView>>(`${this.baseUrl}api/data/audit-ncr-document-view`, { params: params })
            .pipe(map((auditNCRDocuments: any) => {
                const response: PageResponse<AuditNCRDocumentView> = new PageResponse<AuditNCRDocumentView>();
                response.items = auditNCRDocuments.items?.map((row) => {
                    return {
                        updatedBy: row.updatedBy,
                        updatedDate: row.updatedDate,
                        totalNcrCount: row.totalNcrCount,
                        description: row.description,
                        filename: row.filename,
                        ...row
                    };
                });
                response.totalItems = auditNCRDocuments.page.totalItems;
                response.currentPage = auditNCRDocuments.page.currentPage;
                response.itemsPerPage = auditNCRDocuments.page.itemsPerPage;
                response.query = null;
                return response;
            }));
    }

    removeAuditNCRDocument(id: string): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/audit-ncr-document/${id}`);
    }

    getAuditNCRDocument(id: string): Observable<PageResponse<AuditNCRDocument>> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit-ncr-document/${id}`)
            .pipe(map((auditNCRDocument: any) => {
                const ret = new PageResponse<AuditNCRDocument>();
                ret.item = auditNCRDocument.item;
                return ret;
            })
            );
    }

    saveAssignedDocuments(request: any): Observable<any> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/audit-attachment`, request);
    }

    getAuditTypeAssignedDocuments(request: any): Observable<PageResponse<AuditTypePreLoadDocument>> {
        let params = {};
        params = this.getParams(request, params);
        params['find[templateId]'] = 'IS NOT NULL';
        return this._httpClient.get<any>(`${this.baseUrl}api/data/view/audit-type-preload-documents`, { params: params })
            .pipe(
                map((templates: any) => {
                    const response: PageResponse<AuditTypePreLoadDocument> = new PageResponse<AuditTypePreLoadDocument>();
                    response.items = templates.items;
                    response.totalItems = templates.page?.totalItems;
                    response.currentPage = request.currentPage;
                    response.itemsPerPage = request.itemsPerPage;
                    return response;
                })
            );
    }

    getAuditAddendumTemplates(request: any): Observable<PageResponse<AuditAddendumTemplate>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<any>(`${this.baseUrl}api/data/admin/audit-addendum-template`, { params: params })
            .pipe(
                map((templates: any) => {
                    const response: PageResponse<AuditAddendumTemplate> = new PageResponse<AuditAddendumTemplate>();
                    response.items = templates.items;
                    response.totalItems = templates.page?.totalItems;
                    response.currentPage = request.currentPage;
                    response.itemsPerPage = request.itemsPerPage;
                    return response;
                })
            );
    }

    auditorCancelAudit(request: any): Observable<any> {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/workflow/auditor/cancel-audit`, request);
    }

    customerCancelAudit(request: any): Observable<any> {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/workflow/customer/cancel-audit`, request);
    }

    adminCancelAudit(request: any): Observable<any> {
        return this._httpClient.post<PageResponse<any>>(`${this.baseUrl}api/workflow/admin/cancel-audit`, request);
    }

    getAuditReports(request: any): Observable<PageResponse<AuditReportView>> {
        let params = {};
        params = this.getParams(request, params);

        if (request.sort && request.sortDir) {
            params[`sort[${request.sort}]`] = request.sortDir;
        }

        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit-report-view`, { params: params })
            .pipe(map((witnessRecords: any) => {
                const response: PageResponse<AuditReportView> = new PageResponse<AuditReportView>();
                response.items = witnessRecords.items;
                response.totalItems = witnessRecords.page.totalItems;
                response.currentPage = witnessRecords.page.currentPage;
                response.itemsPerPage = witnessRecords.page.itemsPerPage;
                response.query = null;
                return response;
            })
            );
    }

    getAuditorAuditReports(request: any, auditorId): Observable<PageResponse<AuditReportView>> {
        let params = {};
        params = this.getParams(request, params);
        const resolvedAuditorId = auditorId ?? '';
        if (resolvedAuditorId !== '') {
            params['find[auditorId]'] = resolvedAuditorId;
        }

        if (request.sort && request.sortDir) {
            params[`sort[${request.sort}]`] = request.sortDir;
        }

        return this._httpClient.get<any>(`${this.baseUrl}api/data/auditor/audit-report-view`, { params: params })
            .pipe(map((witnessRecords: any) => {
                const response: PageResponse<AuditReportView> = new PageResponse<AuditReportView>();
                response.items = witnessRecords.items;
                response.totalItems = witnessRecords.page.totalItems;
                response.currentPage = witnessRecords.page.currentPage;
                response.itemsPerPage = witnessRecords.page.itemsPerPage;
                response.query = null;
                return response;
            })
            );
    }

    getReport(id: number): Observable<PageResponse<AuditReport>> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit-report/${id}`)
            .pipe(map((attachment: any) => {
                const ret = new PageResponse<AuditReport>();
                ret.item = attachment.item;
                return ret;
            })
            );
    }

    getReportForAuditor(ID: number): Observable<PageResponse<AuditReport>> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/auditor/audit-report/${ID}`)
            .pipe(map((attachment: any) => {
                const ret = new PageResponse<AuditReport>();
                ret.item = attachment.item;
                return ret;
            })
            );
    }

    recalculateManDaysForAudit(request: any): any {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/audit/recalculate-mandays`, request);
    }

    recalculateDurationForAudit(auditId: number): any {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/audit/${auditId}/recalculate-duration`, {});
    }

    removeReport(ID: number): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/audit-report/${ID}`);
    }

    removeReportForAuditor(ID: number): Observable<any> {
        return this._httpClient.delete(`${this.baseUrl}api/data/auditor/audit-report/${ID}`);
    }

    submitReport(ID: number): Observable<any> {
        return this._httpClient.post(`${this.baseUrl}api/data/audit-report/${ID}/submit`, {});
    }

    submitTravelRequest(id: number, payload): Observable<any> {
        return this._httpClient.post(`${this.baseUrl}api/data/auditor/${id}/submit-travel-request`, payload);
    }

    updateTravelRequest(id: number, payload, type): Observable<any> {
        const typeUrl = type == 'approve' ? 'approval-travel-request' : 'reject-travel-request';
        return this._httpClient.post(`${this.baseUrl}api/data/auditor/${id}/${typeUrl}`, payload);
    }

    savePreAuditAnswers(request: Partial<PreAuditQuestion>, isPublic: PreAuditQuestionsScope = ''): Observable<PageResponse<PreAuditQuestion> | null> {
        if (request.id) {
            const { id, ...delta } = request;
            if (Object.keys(delta).length === 0) {
                return of(null);
            }
            delta['id'] = id;
            return this._httpClient.patch<PageResponse<PreAuditQuestion>>(`${this.baseUrl}api/data/${isPublic}pre-audit-questions/${id}`, delta);
        }
        return this._httpClient.post<PageResponse<PreAuditQuestion>>(`${this.baseUrl}api/data/${isPublic}pre-audit-questions`, request);
    }

    serialize(obj): any {
        const str = [];
        for (const p in obj)
            if (obj.hasOwnProperty(p) && obj[p]) {
                str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
            }
        return str.join('&');
    }

    getLocalRequest(requestKey): Observable<any> {
        return this.getLocalFilterById(requestKey);
    }

    saveLocalRequest(requestKey, model): Observable<undefined | boolean> {
        return this.saveLocalFilter(requestKey, model);
    }

    deleteLocalRequest(requestKey): Observable<undefined | boolean> {
        return this._filtersDbService.deleteFilter(requestKey);
    }

    getLocal(): Observable<any> {
        return this.getLocalFilterById(this.requestKey);
    }

    getLocalRiskMatrix(): Observable<any> {
        return this.getLocalFilterById(this.requestKeyRiskMatrix);
    }

    getLocalCertificates(): Observable<any> {
        return this.getLocalFilterById(this.requestCertificateKey);
    }

    saveLocal(model: PageRequest): Observable<any> {
        return this.saveLocalFilter(this.requestKey, model);
    }

    saveLocalRiskMatrix(model: PageRequest): Observable<any> {
        return this.saveLocalFilter(this.requestKeyRiskMatrix, model);
    }

    saveLocalCertificates(model: PageRequest): Observable<any> {
        return this.saveLocalFilter(this.requestCertificateKey, model);
    }

    deleteLocal(): Observable<any> {
        return this._filtersDbService.deleteFilter(this.requestKey);
    }

    deleteAuditForClient(auditId): Observable<any> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/audit/delete-audit-for-client`, { params: auditId });
    }

    getInvoice(request: any): Observable<PageResponse<any>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<any>(`${this.baseUrl}api/data/invoice/get-by-audit/${request.auditIdOnly}`, { params: params })
            .pipe(
                map((invoices: any) => {
                    const response: PageResponse<any> = new PageResponse<any>();
                    response.items = invoices.items?.map((rows: any) => {
                        return {
                            'id': rows?.id,
                            'title': rows?.title,
                            'note': rows?.note,
                            'unitPrice': rows?.unitPrice,
                            'quantity': rows?.quantity,
                            'totalCost': rows?.totalCost,
                            'auditId': rows?.auditId,
                            'salesQuoteLineItemId': rows?.salesQuoteLineItemId,
                            'parentId': rows?.parentId,
                            'invoiceStatusId': rows?.invoiceStatusId,
                            'isOverride': rows?.isOverride,
                        };
                    });
                    response.totalItems = invoices.page?.totalItems;
                    response.currentPage = request.currentPage;
                    response.itemsPerPage = request.itemsPerPage;
                    return response;
                })
            );
    }

    getInvoicesBySalesQuote(request: any): Observable<PageResponse<InvoiceView>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<any>(`${this.baseUrl}api/data/invoice-view`, { params: params })
            .pipe(
                map((invoices: any) => {
                    const response: PageResponse<any> = new PageResponse<InvoiceView>();
                    response.items = invoices.items;
                    response.totalItems = invoices.page?.totalItems;
                    response.currentPage = request.currentPage;
                    response.itemsPerPage = request.itemsPerPage;
                    return response;
                })
            );
    }

    getCertificates(request): Observable<PageResponse<any>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditCertificateView>>(`${this.baseUrl}api/data/admin/client-certificate-view`, { params: params })
            .pipe(map((audits: any) => {
                const response: PageResponse<AuditCertificateView> = new PageResponse<AuditCertificateView>();
                response.items = audits.items.map((rows: any) => {
                    return {
                        clientName: rows.clientName,
                        certificate: rows.certificate,
                        certificateNumber: rows.certificateNumber,
                        standard: rows.standard,
                        industryCodeId: rows?.industryCodeId,
                        certificationType: rows.certificationType,
                        country: rows.country,
                        ballotDate: rows.ballotDate,
                        certificateDate: rows.certificateDate,
                        approvalDate: rows?.approvalDate,
                        revisionDate: rows?.revisionDate,
                        expiryDate: rows?.expiryDate,
                        expiring: rows?.expiring,
                        ...rows
                    }
                });
                response.totalItems = audits.page.totalItems;
                response.currentPage = audits.page.currentPage;
                response.itemsPerPage = audits.page.itemsPerPage;
                response.query = null;
                response.page = audits.page;
                this._auditCertificates.next(response);
                return response;
            }));
    }

    getAuditTrips(request): Observable<PageResponse<AuditTripView>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<AuditTripView>>(`${this.baseUrl}api/data/audit-trip-view`, { params: params })
    }

    getAuditTripsByAuditId(auditId): Observable<PageResponse<AuditTripView>> {
        let params = {};
        const request = {
            itemsPerPage: 1000,
            currentPage: 1,
            query: auditId,
            sort: 'created',
            sortDir: 'asc'
        };
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<AuditTripView>>(`${this.baseUrl}api/data/audit-trip-view`, { params: params })
    }

    getAuditsFromSameYear(auditId: number): Observable<PageResponse<AuditsSameYearByAuditView>> {
        let params = {};
        const request = {
            itemsPerPage: 1000,
            currentPage: 1
        };
        params = this.getParams(request, params);
        params['find'] = {};
        params['find[sourceAuditId]'] = auditId;
        return this._httpClient.get<PageResponse<AuditsSameYearByAuditView>>(`${this.baseUrl}api/data/audits-same-year-by-audit-view`, { params: params })
    }

    getAuditProgramReviewViews(salesQuoteId: string | null, clientId: number, isAdmin: boolean = false): Observable<PageResponse<AuditProgramReviewView>> {
        let params = {};
        const request = {
            itemsPerPage: 1000,
            currentPage: 1,
            salesQuoteId: salesQuoteId,
            clientId: clientId,
            type: isAdmin ? 'admin' : 'auditor'
        };
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<AuditProgramReviewView>>(`${this.baseUrl}api/data/audit-program-review-view`, { params: params })
    }

    saveAuditProgramReview(auditProgramReview: AuditProgramReviewView): Observable<AuditProgramReview> {
        const payload: Partial<AuditProgramReviewView> = { ...auditProgramReview };
        delete payload.auditType;
        delete payload.auditTypeId;
        delete payload.salesQuoteId;
        delete payload.year;
        payload.ncrs = Number(payload.minorNcrs) + Number(payload.majorNcrs);

        const entity = payload as AuditProgramReview;
        return entity.id
            ? this.updateAuditProgramReview(entity)
            : this.createAuditProgramReview(entity);
    }

    createAuditProgramReview(auditProgramReview: AuditProgramReview): Observable<AuditProgramReview> {
        return this._httpClient.post<PageResponse<AuditProgramReview>>(`${this.baseUrl}api/data/admin/audit-program-review`, auditProgramReview)
            .pipe(map((res: PageResponse<AuditProgramReview>) => res.item));
    }

    updateAuditProgramReview(auditProgramReview: AuditProgramReview): Observable<AuditProgramReview> {
        return this._httpClient.put<PageResponse<AuditProgramReview>>(`${this.baseUrl}api/data/admin/audit-program-review/${auditProgramReview.id}`, auditProgramReview)
            .pipe(map((res: PageResponse<AuditProgramReview>) => res.item));
    }

    getAuditorAuditProgramReviewViews(salesQuoteId: string | null, clientId: number, auditorId: number): Observable<PageResponse<AuditorAuditProgramReviewView>> {
        let params = {};
        const request = {
            itemsPerPage: 1000,
            currentPage: 1,
            salesQuoteId: salesQuoteId,
            clientId: clientId,
            auditorId: auditorId
        }
        params = this.getParams(request, params);
        return this._httpClient.get<PageResponse<AuditorAuditProgramReviewView>>(`${this.baseUrl}api/data/auditor-audit-program-review-view`, { params: params })
    }

    createAuditorAuditProgramReview(auditorAuditProgramReview: AuditorAuditProgramReview): Observable<AuditorAuditProgramReview> {
        const request = {
            AuditId: auditorAuditProgramReview.auditId,
            AuditorId: auditorAuditProgramReview.auditorId,
            MinorNcrs: auditorAuditProgramReview.minorNcrs,
            MajorNcrs: auditorAuditProgramReview.majorNcrs,
            CustomerComplaints: auditorAuditProgramReview.customerComplaints,
            Comments: auditorAuditProgramReview.comments,
        }

        return this._httpClient.post<AuditorAuditProgramReview>(`${this.baseUrl}api/data/admin/auditor-audit-program-review`, request);
    }

    updateAuditorAuditProgramReview(auditorAuditProgramReview: AuditorAuditProgramReview): Observable<AuditorAuditProgramReview> {
        return this._httpClient.put<AuditorAuditProgramReview>(`${this.baseUrl}api/data/admin/auditor-audit-program-review/${auditorAuditProgramReview.id}`, auditorAuditProgramReview);
    }

    exportAuditorNcrsReport(): Observable<any> {
        const params = {};
        params['fileType'] = 'xlsx';

        return this._httpClient.get(`${this.baseUrl}api/data/admin/auditor-audit-program-review-export-view/export`, { params: params, responseType: 'arraybuffer' })
    }

    getSiteTripsBySiteId(siteId: string, auditTrips): AuditTripView[] {
        return auditTrips?.filter(t => t.quoteRequestLocationId === siteId)?.map((rows) => {
            return {
                auditorName: rows.auditorName,
                startDate: rows.startDate,
                endDate: rows.endDate,
                daysOnSite: rows.daysOnSite,
                daysOffSite: rows.daysOffSite,
                ...rows
            };
        });
    }

    getQuoteRequestLocationByAuditViews(auditId: number): Observable<PageResponse<QuoteRequestLocationAuditView>> {
        return this._httpClient
            .get<PageResponse<QuoteRequestLocationAuditView>>(`${this.baseUrl}api/data/quote-request-location-audit-view`,
                {
                    params: { 'itemsPerPage': 100, 'find[AuditId]': auditId }
                })
    }

    sendEmailNotification(auditId: number, request: any): Observable<boolean> {
        return this._httpClient.post<boolean>(`${this.baseUrl}api/data/audit/${auditId}/notification`, request);
    }

    checkSuspendedCertificate(
        certRes: { items?: Array<{ certificateStatusId?: unknown }> },
        options: { certificateStatusList?: Array<{ label?: string; text?: string; value?: string | number }> }
    ): boolean {
        const suspendedStatus = options?.certificateStatusList?.find(opt => (opt.label || opt.text)?.toLowerCase() === 'suspended');
        const suspendedStatusId = suspendedStatus?.value;
        const hasSuspendedCertificate = certRes.items?.find((cert: AuditCertificateView) => {
            return String(cert.certificateStatusId) === String(suspendedStatusId);
        });
        if (hasSuspendedCertificate) {
            this.alertService.errorToast(
                this.schedulingBlockedTitle,
                this.schedulingBlockedReason
            );
            return true;
        }
        return false;
    }

    getAuditZipFile(auditId: number): Observable<void> {
        return this._httpClient.get(`${this.baseUrl}api/data/audit-attachment/download/${auditId}`, { responseType: 'blob' })
            .pipe(
                map((blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `audit-${auditId}-attachments.zip`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                })
            );
    }

    analyzeAudit(auditId: number): Observable<boolean> {
        return this._httpClient.post(`${this.baseUrl}api/data/audit/${auditId}/analyze`, {}).pipe(
            map(() => {
                return true;
            })
        );
    }

    clearCache(): void {
        this._audits.next(null);
    }

    getAuditBallotHistory(request: PageRequest,
        options: {
            ballotTypeList?: Array<{ label?: string; text?: string; value?: string | number }>,
            ballotStatusList?: Array<{ label?: string; text?: string; value?: string | number }>
        }
    ): Observable<PageResponse<AuditBallotHistoryView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditBallotHistoryView>>(`${this.baseUrl}api/data/view/audit/ballot/history`, { params: params })
            .pipe(map((auditBallotHistory: PageResponse<AuditBallotHistoryView>) => {
                const response: PageResponse<AuditBallotHistoryView> = new PageResponse<AuditBallotHistoryView>();
                response.items = auditBallotHistory.items?.map((row) => {
                    let parsedData = null;
                    try {
                        parsedData = row?.valueAfter ? JSON.parse(row.valueAfter) : null;
                    } catch {
                        parsedData = null;
                    }
                    const createdByUser = parsedData?.CreatedByName;
                    const ballotType = parsedData?.BallotTypeId;
                    const ballotStatus = parsedData?.BallotStatusId;
                    return {
                        ...row,
                        activityUser: row?.activityUser ?? createdByUser,
                        activityDate: row?.created ?? null,
                        ballotType: options?.ballotTypeList?.find(rowData => rowData?.value == ballotType)?.label,
                        ballotStatus: options?.ballotStatusList?.find(rowData => rowData?.value == ballotStatus)?.label,
                    };
                });
                response.totalItems = auditBallotHistory.page.totalItems;
                response.currentPage = auditBallotHistory.page.currentPage;
                response.itemsPerPage = auditBallotHistory.page.itemsPerPage;
                response.query = null;
                this._auditBallot.next(response);
                return response;
            }));
    }

    getAuditRiskAssessmentHistory(request: PageRequest): Observable<PageResponse<AuditRiskAssessmentHistoryView>> {
        let params = {};
        params = this.getParams(request, params);

        return this._httpClient.get<PageResponse<AuditRiskAssessmentHistoryView>>(`${this.baseUrl}api/data/admin/view/risk-assessment/history`, { params: params })
            .pipe(map((auditRiskAssessmentHistory: PageResponse<AuditRiskAssessmentHistoryView>) => {
                const response: PageResponse<AuditRiskAssessmentHistoryView> = new PageResponse<AuditRiskAssessmentHistoryView>();
                response.items = auditRiskAssessmentHistory.items?.map((item) => {
                    let parsedValueAfter = null;
                    try {
                        parsedValueAfter = item?.valueAfter ? JSON.parse(item.valueAfter) : null;
                    } catch {
                        parsedValueAfter = null;
                    }

                    let parsedValueBefore = null;
                    try {
                        parsedValueBefore = item?.valueBefore ? JSON.parse(item.valueBefore) : null;
                    } catch {
                        parsedValueBefore = null;
                    }

                    item.parsedValueAfter = parsedValueAfter;
                    item.parsedValueBefore = parsedValueBefore;
                    item.originalDaysOnSite = item.parsedValueBefore?.DaysOnSite;
                    item.originalDaysOffSite = item.parsedValueBefore?.DaysOffSite;
                    item.finalDaysOnSite = item.parsedValueAfter?.FinalDaysOnSite;
                    item.finalDaysOffSite = item.parsedValueAfter?.FinalDaysOffSite;
                    return item;
                });
                response.totalItems = auditRiskAssessmentHistory.page.totalItems;
                response.currentPage = auditRiskAssessmentHistory.page.currentPage;
                response.itemsPerPage = auditRiskAssessmentHistory.page.itemsPerPage;
                response.query = null;
                response.page = auditRiskAssessmentHistory.page;
                this._auditRiskAssessment.next(response);
                return response;
            }));
    }

    saveLocalFilter(requestKey, model): Observable<undefined | boolean> {
        return this._filtersDbService.saveFilter({
            id: requestKey,
            criteria: model,
            updatedAt: Date.now()
        });
    }

    getLocalFilterById(id: string): Observable<any> {
        return this._filtersDbService.getById(id)
            .pipe(
                map(data => new PageRequest().deserialize(data?.criteria))
            );
    }

    private mapToRequirementsView(requirements: AuditDocumentationRequirement[] | null | undefined): AuditDocumentationRequirementsView[] | null {
        if (!requirements) return null;
        return requirements.map(requirement => ({
            id: requirement.id,
            auditType: requirement.auditTypeDisplayName,
            auditConfirmation: requirement.doc_AuditConfirmation || false,
            auditPlan: requirement.doc_AuditPlan || false,
            auditReport: requirement.doc_AuditReport || false,
            prePlanning: requirement.doc_PrePlanning || false,
            addendum: requirement.doc_Addendum || false,
            transferChecklist: requirement.doc_TransferChecklist || false,
            technicalChecklist: requirement.technicalChecklist || false,
            expenseReport: requirement.expenseReport || false,
        } as AuditDocumentationRequirementsView));
    }
}
